import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../api';
import BeyondPresenceCosting from './BeyondPresenceCosting';
import './AiCosting.css';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  ReferenceLine
} from 'recharts';
import {
  RefreshCw,
  ExternalLink,
  Download,
  Search,
  Building2,
  Cpu,
  Layers,
  Users,
  DollarSign,
  Coins,
  TrendingUp,
  Eye,
  X,
  Calendar,
  CheckCircle2,
  Info,
  ChevronDown,
  FileText,
  FileSpreadsheet,
  Wallet,
  CreditCard,
  PiggyBank
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const SERVICE_COLORS = ['#168cf5', '#10b981', '#f59e0b', '#7557ee', '#64748b'];

const formatIndianNumber = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '0';
  const numStr = Math.round(Number(val)).toString();
  if (numStr.length <= 3) return numStr;
  const lastThree = numStr.substring(numStr.length - 3);
  const otherNumbers = numStr.substring(0, numStr.length - 3);
  return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree;
};

const formatUSD = (val) => {
  const num = Number(val || 0);
  return '$' + num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatINR = (val) => {
  const num = Number(val || 0);
  return '₹ ' + num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatRangeLabel = (from, to) => {
  if (!from || !to) return 'All time';
  const fmt = (d) => {
    const dt = new Date(d + 'T00:00:00');
    if (isNaN(dt)) return d;
    return dt.toLocaleDateString('en-US', { month: 'short', day: '2-digit' });
  };
  return `${fmt(from)} - ${fmt(to)}`;
};

const OpenRouterCosting = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncSuccess, setSyncSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Export dropdown state
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);

  // Core Data States
  const [summary, setSummary] = useState(null);
  const [uniCostingData, setUniCostingData] = useState({ rows: [], total_summary: null });
  const [universities, setUniversities] = useState([]);
  const [models, setModels] = useState([]);
  const [services, setServices] = useState([]);
  const [students, setStudents] = useState([]);
  const [logs, setLogs] = useState([]);
  const [openRouterInfo, setOpenRouterInfo] = useState(null);
  const [wallet, setWallet] = useState(null);

  // Active View States
  const [activeTab, setActiveTab] = useState('university'); // 'university' | 'model' | 'service' | 'student' | 'logs'
  const [search, setSearch] = useState('');
  const [selectedBottomUni, setSelectedBottomUni] = useState('');

  // Filters (defaults to the current month, no hardcoded dates)
  const today = new Date();
  const monthStart = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-01`;
  const monthEnd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;
  const [filters, setFilters] = useState({
    from: monthStart,
    to: monthEnd,
    university: '',
    provider: '',
    model: ''
  });

  // University Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalUniversity, setModalUniversity] = useState(null);

  // Fetch Live OpenRouter Metadata
  const loadOpenRouterMeta = async () => {
    try {
      const res = await api.get('/admin/ai-costing/openrouter-info');
      setOpenRouterInfo(res.data);
    } catch (err) {
      console.warn('Could not fetch OpenRouter info:', err);
    }
  };

  // Fetch live OpenRouter wallet (credits / used / remaining)
  const loadWallet = async (refresh = false) => {
    try {
      const res = await api.get('/admin/ai-costing/wallet', { params: refresh ? { refresh: true } : {} });
      setWallet(res.data);
    } catch (err) {
      console.warn('Could not fetch OpenRouter wallet:', err);
    }
  };

  // Sync pricing directly with OpenRouter API
  const handleSyncPricing = async () => {
    setSyncing(true);
    setSyncSuccess(false);
    try {
      const res = await api.get('/admin/ai-costing/sync-pricing');
      if (res.data) {
        setSyncSuccess(true);
        loadOpenRouterMeta();
        loadDashboard();
        loadWallet(true);
        setTimeout(() => setSyncSuccess(false), 4000);
      }
    } catch (err) {
      // fallback to POST if get isn't registered
      try {
        await api.post('/admin/ai-costing/sync-pricing');
        setSyncSuccess(true);
        loadOpenRouterMeta();
        loadDashboard();
        loadWallet(true);
        setTimeout(() => setSyncSuccess(false), 4000);
      } catch (e2) {
        console.error('Pricing sync failed:', e2);
      }
    } finally {
      setSyncing(false);
    }
  };

  // Main Dashboard Data Loader
  const loadDashboard = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.from) params.from_date = filters.from;
      if (filters.to) params.to_date = filters.to;
      if (filters.university) params.university_id = filters.university;
      if (filters.provider) params.provider = filters.provider;
      if (filters.model) params.model_id = filters.model;

      const results = await Promise.allSettled([
        api.get('/admin/ai-costing/summary', { params }),
        api.get('/admin/ai-costing/universities-costing', { params }),
        api.get('/admin/ai-costing/universities'),
        api.get('/admin/ai-costing/models', { params }),
        api.get('/admin/ai-costing/services', { params }),
        api.get('/admin/ai-costing/students-usage', { params }),
        api.get('/admin/ai-costing/usage-logs', { params })
      ]);

      const [sumRes, uniCostRes, uniListRes, modRes, srvRes, stuRes, logRes] = results;

      if (sumRes.status === 'fulfilled') {
        setSummary(sumRes.value.data);
      }
      if (uniCostRes.status === 'fulfilled') {
        setUniCostingData(uniCostRes.value.data || { rows: [], total_summary: null });
      }
      const uList = uniListRes.status === 'fulfilled' ? (uniListRes.value.data || []) : [];
      setUniversities(uList);
      if (modRes.status === 'fulfilled') {
        setModels(modRes.value.data || []);
      }
      if (srvRes.status === 'fulfilled') {
        setServices(srvRes.value.data || []);
      }
      if (stuRes.status === 'fulfilled') {
        setStudents(stuRes.value.data || []);
      }
      if (logRes.status === 'fulfilled') {
        setLogs(logRes.value.data || []);
      }

      if (!selectedBottomUni && uList.length > 0) {
        setSelectedBottomUni(uList[0].id);
      }

      // If critical summary failed or all failed, surface error message
      const allFailed = results.every((r) => r.status === 'rejected');
      if (allFailed || sumRes.status === 'rejected') {
        const err = sumRes.status === 'rejected' ? sumRes.reason : results.find((r) => r.status === 'rejected')?.reason;
        setError('Unable to load live costing data. ' + (err?.response?.data?.detail || err?.message || 'Server error'));
      }
    } catch (err) {
      console.error(err);
      setError('Unable to load live costing data. ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOpenRouterMeta();
    loadWallet();
  }, []);

  // Close export dropdown on outside click
  useEffect(() => {
    const closeMenu = (e) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, []);

  useEffect(() => {
    loadDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleDatePreset = (preset) => {
    if (preset === 'current_month') {
      setFilters((prev) => ({ ...prev, from: monthStart, to: monthEnd }));
    } else if (preset === 'all_time') {
      setFilters((prev) => ({ ...prev, from: '', to: '' }));
    }
  };

  const openUniversityModal = (uniRow) => {
    setModalUniversity(uniRow);
    setModalOpen(true);
  };

  // CSV Export
  const exportCSV = () => {
    const data = logs;
    if (!data.length) {
      alert('No usage logs to export. Use “PDF Report” to download the full dashboard report.');
      return;
    }
    const headers = [
      'ID',
      'Date',
      'University Name',
      'Student Name',
      'User Type',
      'AI Service',
      'Provider',
      'Actual Model',
      'Input Tokens',
      'Cached Input Tokens',
      'Output Tokens',
      'Total Tokens',
      'Cost USD',
      'Cost INR'
    ];
    const csvRows = [
      headers,
      ...data.map((x) => [
        x.id,
        x.created_at,
        x.university_name,
        x.student_name || '',
        x.user_type,
        x.service_name,
        x.provider,
        x.model_name || x.actual_model_id,
        x.input_tokens,
        x.cached_input_tokens,
        x.output_tokens,
        x.total_tokens,
        x.cost_usd,
        x.cost_inr
      ])
    ]
      .map((r) => r.map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csvRows], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ai-costing-report-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  // PDF Report Export
  const exportPDF = () => {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageWidth = doc.getPageWidth();
    const pageHeight = doc.getPageHeight();
    const margin = 30;
    const period = formatRangeLabel(filters.from, filters.to);

    // Header band
    doc.setFillColor(15, 23, 42);
    doc.rect(0, 0, pageWidth, 64, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(17);
    doc.text('AI Costing Report', margin, 26);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(200, 215, 235);
    doc.text(`Period: ${period}   |   Generated: ${new Date().toLocaleString()}`, margin, 44);

    let y = 84;

    const sectionTitle = (title) => {
      if (y > pageHeight - 60) {
        doc.addPage();
        y = margin + 10;
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.setTextColor(30, 41, 59);
      doc.text(title, margin, y);
      y += 6;
    };

    const addTable = (head, body, opts = {}) => {
      const table = autoTable(doc, {
        startY: y,
        margin: { left: margin, right: margin },
        head,
        body,
        theme: 'grid',
        headStyles: { fillColor: [22, 140, 245], textColor: 255, fontSize: 8, fontStyle: 'bold' },
        bodyStyles: { fontSize: 8, textColor: [30, 41, 59] },
        alternateRowStyles: { fillColor: [245, 248, 252] },
        styles: { cellPadding: 4, lineColor: [226, 232, 240], lineWidth: 0.5 },
        didDrawPage: () => {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(148, 163, 184);
          doc.text(`AI Costing Report — ${period}`, margin, pageHeight - 16);
          doc.text(`Page ${doc.getNumberOfPages()}`, pageWidth - margin, pageHeight - 16, {
            align: 'right'
          });
        },
        ...opts
      });
      y = table.finalY + 18;
    };

    // 1. Summary KPIs
    if (summary) {
      sectionTitle('Summary');
      addTable(
        [['Metric', 'Value']],
        [
          ['Total Tokens', formatIndianNumber(summary.total_tokens)],
          ['University Tokens', formatIndianNumber(summary.university_tokens)],
          ['Student Tokens', formatIndianNumber(summary.student_tokens)],
          ['Total Cost (USD)', formatUSD(summary.total_cost_usd)],
          ['Total Cost (INR)', formatINR(summary.total_cost_inr)],
          ['Exchange Rate', summary.usd_inr_rate ? `1 USD = ₹ ${summary.usd_inr_rate.toFixed(2)}` : '—']
        ],
        { columnStyles: { 0: { fontStyle: 'bold' } } }
      );
    }

    // 2. University Wise
    const uniRows = uniCostingData.rows || [];
    if (uniRows.length > 0) {
      sectionTitle('University Wise Usage');
      addTable(
        ['#', 'University', 'Students', 'University Tokens', 'Student Tokens', 'Total Tokens', 'Cost (USD)', 'Cost (INR)'],
        uniRows.map((r, i) => [
          String(i + 1),
          r.university_name,
          r.students_count,
          formatIndianNumber(r.university_tokens),
          formatIndianNumber(r.student_tokens),
          formatIndianNumber(r.total_tokens),
          formatUSD(r.cost_usd),
          formatINR(r.cost_inr)
        ])
      );
    }

    // 3. Model Wise
    if (models.length > 0) {
      sectionTitle('Model Wise Usage');
      addTable(
        ['Provider', 'Model', 'Model ID', 'Total Tokens', 'Cost (USD)', 'Cost (INR)'],
        models.map((m) => [
          m.provider,
          m.name,
          m.id,
          formatIndianNumber(m.total_tokens),
          formatUSD(m.cost_usd),
          formatINR(m.cost_inr)
        ])
      );
    }

    // 4. Service Wise
    if (services.length > 0) {
      sectionTitle('Service Wise Usage');
      addTable(
        ['AI Service', 'Total Tokens', 'Cost (USD)', 'Cost (INR)', '% of AI Spend'],
        services.map((s) => [
          s.service_name,
          formatIndianNumber(s.total_tokens),
          formatUSD(s.cost_usd),
          formatINR(s.cost_inr),
          `${s.percentage}%`
        ])
      );
    }

    // 5. Student Wise
    if (students.length > 0) {
      sectionTitle('Student Wise Usage');
      addTable(
        ['#', 'Student', 'University', 'Total Tokens', 'Cost (USD)', 'Cost (INR)'],
        students.slice(0, 50).map((st, i) => [
          String(i + 1),
          st.student_name,
          st.university_name,
          formatIndianNumber(st.total_tokens),
          formatUSD(st.cost_usd),
          formatINR(st.cost_inr)
        ])
      );
    }

    // 6. Detailed usage logs
    if (logs.length > 0) {
      sectionTitle('Detailed Usage Logs');
      addTable(
        ['Timestamp', 'University', 'User', 'Service', 'Model', 'Total', 'USD', 'INR'],
        logs.slice(0, 100).map((l) => [
          l.created_at ? new Date(l.created_at).toLocaleString() : '—',
          l.university_name,
          l.user_type === 'STUDENT' ? l.student_name || 'Student' : 'University',
          l.service_name,
          l.model_name || l.actual_model_id,
          formatIndianNumber(l.total_tokens),
          formatUSD(l.cost_usd),
          formatINR(l.cost_inr)
        ])
      );
    }

    // 7. No-data fallback note
    if (!summary && uniRows.length === 0 && models.length === 0) {
      sectionTitle('No data available for the selected period.');
    }

    doc.save(`ai-costing-report-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  // Filtered University Rows
  const filteredUniRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return uniCostingData.rows || [];
    return (uniCostingData.rows || []).filter((r) =>
      (r.university_name || '').toLowerCase().includes(q)
    );
  }, [uniCostingData.rows, search]);

  // Filtered Model Rows
  const filteredModelRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return models;
    return models.filter(
      (m) =>
        (m.name || '').toLowerCase().includes(q) ||
        (m.id || '').toLowerCase().includes(q) ||
        (m.provider || '').toLowerCase().includes(q)
    );
  }, [models, search]);

  // Filtered Service Rows
  const filteredServiceRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return services;
    return services.filter((s) => (s.service_name || '').toLowerCase().includes(q));
  }, [services, search]);

  // Filtered Student Rows
  const filteredStudentRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter(
      (st) =>
        (st.student_name || '').toLowerCase().includes(q) ||
        (st.university_name || '').toLowerCase().includes(q)
    );
  }, [students, search]);

  // Filtered Usage Logs
  const filteredLogRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter(
      (l) =>
        (l.university_name || '').toLowerCase().includes(q) ||
        (l.student_name || '').toLowerCase().includes(q) ||
        (l.service_name || '').toLowerCase().includes(q) ||
        (l.model_name || '').toLowerCase().includes(q) ||
        (l.provider || '').toLowerCase().includes(q)
    );
  }, [logs, search]);

  // Modal logs calculation
  const modalStats = useMemo(() => {
    if (!modalUniversity) return { tokens: 0, ut: 0, st: 0, usd: 0, inr: 0, logs: [] };
    const uId = modalUniversity.id || modalUniversity.university_id;
    const uLogs = logs.filter((l) => l.university_id === uId);
    let ut = 0;
    let st = 0;
    let usd = 0;
    let inr = 0;
    let tokens = 0;
    uLogs.forEach((l) => {
      tokens += Number(l.total_tokens || 0);
      usd += Number(l.cost_usd || 0);
      inr += Number(l.cost_inr || 0);
      if (l.user_type === 'UNIVERSITY') ut += Number(l.total_tokens || 0);
      else st += Number(l.total_tokens || 0);
    });
    return { tokens, ut, st, usd, inr, logs: uLogs };
  }, [modalUniversity, logs]);

  // Bottom Card 1: Selected University AI Services
  const bottomUniServices = useMemo(() => {
    const uId = Number(selectedBottomUni);
    const uLogs = logs.filter((l) => l.university_id === uId);
    const map = {};
    uLogs.forEach((l) => {
      const sName = l.service_name || 'General';
      if (!map[sName]) {
        map[sName] = {
          service_name: sName,
          models: new Set(),
          tokens: 0,
          usd: 0,
          inr: 0
        };
      }
      map[sName].models.add(l.model_name || l.actual_model_id);
      map[sName].tokens += Number(l.total_tokens || 0);
      map[sName].usd += Number(l.cost_usd || 0);
      map[sName].inr += Number(l.cost_inr || 0);
    });
    return Object.values(map);
  }, [selectedBottomUni, logs]);

  // Bottom Card 2: Student Usage
  const bottomStudents = useMemo(() => {
    return students.slice(0, 5);
  }, [students]);

  // Bottom Card 3: Model Usage
  const bottomModels = useMemo(() => {
    return models.slice(0, 5);
  }, [models]);

  // Donut chart total cost
  const totalCostUsdValue = summary?.total_cost_usd ?? 0;

  return (
    <div className="ai-costing-dashboard">
      {/* Top OpenRouter Live Banner */}
      <div className="openrouter-status-strip">
        <div className="openrouter-strip-left">
          <span className="live-pulse"></span>
          <span className="openrouter-title">OpenRouter Live Integration</span>
          <span className="badge-workspace">
            Workspace: <strong>default</strong>
          </span>
          <span className="badge-fee">{openRouterInfo?.fee_percentage != null ? `${openRouterInfo.fee_percentage}% Platform Fee` : 'Platform Fee'}</span>
          {openRouterInfo?.total_models && (
            <span className="badge-models">{openRouterInfo.total_models} Models Available</span>
          )}
        </div>
        <div className="openrouter-strip-right">
          <button
            className={`sync-pricing-btn ${syncing ? 'spinning' : ''}`}
            onClick={handleSyncPricing}
            disabled={syncing}
            title="Fetch live model pricing from OpenRouter API"
          >
            <RefreshCw size={14} className={syncing ? 'spin-icon' : ''} />
            {syncing ? 'Syncing...' : 'Sync Live Pricing'}
          </button>
          <a
            href="https://openrouter.ai/workspaces/default"
            target="_blank"
            rel="noopener noreferrer"
            className="openrouter-link-btn"
          >
            OpenRouter Workspace <ExternalLink size={13} />
          </a>
        </div>
      </div>

      {syncSuccess && (
        <div className="toast-success">
          <CheckCircle2 size={16} /> Live OpenRouter model pricing synchronized successfully.
        </div>
      )}

      {/* Header Section */}
      <div className="header-section">
        <div className="header-titles">
          <h1 className="costing-title">AI Costing</h1>
          <p className="costing-subtitle">
            Track AI usage, tokens, and actual cost across universities, students and AI services.
          </p>
        </div>

        {/* Top Filters Bar */}
        <div className="filters-bar">
          <div className="date-range-group">
            <span className="filter-icon">
              <Calendar size={14} />
            </span>
            <input
              type="date"
              name="from"
              value={filters.from}
              onChange={handleFilterChange}
              className="filter-date"
              title="Start Date"
            />
            <span className="date-sep">to</span>
            <input
              type="date"
              name="to"
              value={filters.to}
              onChange={handleFilterChange}
              className="filter-date"
              title="End Date"
            />
            <button
              type="button"
              className={`preset-btn ${filters.from === monthStart && filters.to === monthEnd ? 'active' : ''}`}
              onClick={() => handleDatePreset('current_month')}
              title={`Quick Select: ${formatRangeLabel(monthStart, monthEnd)}`}
            >
              Current Month
            </button>
          </div>

          <div className="select-group">
            <select
              name="university"
              value={filters.university}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Universities</option>
              {universities.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </div>

          <div className="select-group">
            <select
              name="provider"
              value={filters.provider}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Providers</option>
              {[...new Set(models.map((m) => m.provider).filter(Boolean))].map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>

          <div className="select-group">
            <select
              name="model"
              value={filters.model}
              onChange={handleFilterChange}
              className="filter-select"
            >
              <option value="">All Models</option>
              {models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          <div className="export-menu-wrap" ref={exportMenuRef}>
            <button
              onClick={() => setExportMenuOpen((o) => !o)}
              className="export-action-btn"
              title="Export report"
            >
              <Download size={14} /> Export
              <ChevronDown size={13} className={`export-caret ${exportMenuOpen ? 'open' : ''}`} />
            </button>
            {exportMenuOpen && (
              <div className="export-menu">
                <button
                  className="export-menu-item"
                  onClick={() => {
                    setExportMenuOpen(false);
                    exportPDF();
                  }}
                  title="Download a formatted PDF report of the whole dashboard"
                >
                  <FileText size={13} /> PDF Report
                </button>
                <button
                  className="export-menu-item"
                  onClick={() => {
                    setExportMenuOpen(false);
                    exportCSV();
                  }}
                  title="Download raw usage logs as CSV"
                >
                  <FileSpreadsheet size={13} /> CSV Export
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="costing-error-banner">{error}</div>}

      {/* Main Content Area */}
      <div className={`costing-body ${loading ? 'loading-mask' : ''}`}>
        {/* OpenRouter Wallet Cards (live credits from OpenRouter API) */}
        <div className="wallet-grid">
          <div className="kpi-card wallet-card">
            <div className="kpi-icon-wrap icon-blue">
              <Wallet size={22} />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">Wallet (OpenRouter)</div>
              <div className="kpi-value">
                {wallet?.wallet?.total_credits != null ? formatUSD(wallet.wallet.total_credits) : '—'}
              </div>
              <div className="kpi-subtext">
                {wallet?.source === 'openrouter_api' ? 'Live from OpenRouter' : wallet?.error ? wallet.error : '—'}
              </div>
            </div>
          </div>

          <div className="kpi-card wallet-card">
            <div className="kpi-icon-wrap icon-purple">
              <CreditCard size={22} />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">Used</div>
              <div className="kpi-value">
                {wallet?.wallet?.total_usage != null ? formatUSD(wallet.wallet.total_usage) : '—'}
              </div>
              <div className="kpi-subtext">
                {wallet?.wallet?.usage_percent != null ? `${wallet.wallet.usage_percent}% of wallet` : '—'}
                {wallet?.total_tokens != null ? ` · ${formatIndianNumber(wallet.total_tokens)} tokens` : ''}
              </div>
            </div>
          </div>

          <div className="kpi-card wallet-card">
            <div className="kpi-icon-wrap icon-green">
              <PiggyBank size={22} />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">Remaining</div>
              <div className="kpi-value">
                {wallet?.wallet?.remaining_credits != null ? formatUSD(wallet.wallet.remaining_credits) : '—'}
              </div>
              <div className="kpi-subtext">
                {wallet?.fetched_at ? `Updated ${new Date(wallet.fetched_at).toLocaleTimeString()}` : '—'}
              </div>
            </div>
          </div>

          {/* Usage bar card */}
          <div className="kpi-card wallet-card wallet-usage-card">
            <div className="kpi-content">
              <div className="kpi-label">Wallet Usage</div>
              <div className="wallet-usage-bar">
                <div
                  className="wallet-usage-fill"
                  style={{ width: `${Math.min(wallet?.wallet?.usage_percent || 0, 100)}%` }}
                />
              </div>
              <div className="kpi-subtext">
                {wallet?.wallet?.usage_percent != null
                  ? `${wallet.wallet.usage_percent}% used · ${formatUSD(wallet.wallet.remaining_credits || 0)} left`
                  : 'Wallet data unavailable'}
              </div>
            </div>
          </div>
        </div>

        {/* Top 5 Metric Cards (KPI Summary) */}
        <div className="kpi-grid">
          {/* Total Tokens */}
          <div className="kpi-card">
            <div className="kpi-icon-wrap icon-blue">
              <Coins size={22} />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">Total Tokens Used</div>
              <div className="kpi-value">
                {summary ? formatIndianNumber(summary.total_tokens) : '—'}
              </div>
              <div className="kpi-trend positive">
                <TrendingUp size={12} />
                <span>{summary && summary.previous_period_change_percent != null ? `${summary.previous_period_change_percent > 0 ? '+' : ''}${summary.previous_period_change_percent}% vs previous month` : 'vs previous month'}</span>
              </div>
            </div>
          </div>

          {/* University Tokens */}
          <div className="kpi-card">
            <div className="kpi-icon-wrap icon-cyan">
              <Building2 size={22} />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">University Tokens Used</div>
              <div className="kpi-value">
                {summary ? formatIndianNumber(summary.university_tokens) : '—'}
              </div>
              <div className="kpi-subtext">{summary && summary.total_tokens && summary.university_tokens != null ? `${((summary.university_tokens / summary.total_tokens) * 100).toFixed(1)}% of total tokens` : '—'}</div>
            </div>
          </div>

          {/* Student Tokens */}
          <div className="kpi-card">
            <div className="kpi-icon-wrap icon-green">
              <Users size={22} />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">Student Tokens Used</div>
              <div className="kpi-value">
                {summary ? formatIndianNumber(summary.student_tokens) : '—'}
              </div>
              <div className="kpi-subtext">{summary && summary.total_tokens && summary.student_tokens != null ? `${((summary.student_tokens / summary.total_tokens) * 100).toFixed(1)}% of total tokens` : '—'}</div>
            </div>
          </div>

          {/* Total Cost USD */}
          <div className="kpi-card">
            <div className="kpi-icon-wrap icon-purple">
              <DollarSign size={22} />
            </div>
            <div className="kpi-content">
              <div className="kpi-label">Total Cost (USD)</div>
              <div className="kpi-value">
                {summary ? formatUSD(summary.total_cost_usd) : '—'}
              </div>
              <div className="kpi-subtext">Includes {summary?.fee_percentage != null ? `${summary.fee_percentage}%` : ''} platform fee</div>
            </div>
          </div>

          {/* Total Cost INR */}
          <div className="kpi-card">
            <div className="kpi-icon-wrap icon-orange">
              <span className="inr-symbol">₹</span>
            </div>
            <div className="kpi-content">
              <div className="kpi-label">Total Cost (INR)</div>
              <div className="kpi-value">
                {summary ? formatINR(summary.total_cost_inr) : '—'}
              </div>
              <div className="kpi-subtext">@ {summary?.usd_inr_rate ? `₹${summary.usd_inr_rate.toFixed(2)} / USD` : '—'}</div>
            </div>
          </div>
        </div>

        {/* 3 Analytics Charts (3 Columns Layout) */}
        <div className="analytics-grid">
          {/* Chart 1: Dual-line Area/Line Chart */}
          <div className="analytics-card">
            <div className="analytics-header">
              <div>
                <h3 className="analytics-title">Token Usage Trend</h3>
                <span className="analytics-subtitle">{formatRangeLabel(filters.from, filters.to)}</span>
              </div>
              <div className="chart-legend-top">
                <span className="legend-chip">
                  <span className="chip-dot dot-student"></span> Student Tokens
                </span>
                <span className="legend-chip">
                  <span className="chip-dot dot-university"></span> University Tokens
                </span>
              </div>
            </div>
            {summary?.trend_source === 'historical' && (
              <div className="analytics-note">Showing accumulated usage — daily detail appears once new AI calls are logged</div>
            )}
            <div className="chart-container area-chart-wrap">
              <ResponsiveContainer width="100%" height={210}>
                <AreaChart
                  data={summary?.trend || []}
                  margin={{ top: 10, right: 10, left: -18, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="studentGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#168cf5" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#168cf5" stopOpacity={0.0} />
                    </linearGradient>
                    <linearGradient id="universityGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(200, 215, 230, 0.4)" />
                  {summary?.trend_source === 'historical' && (summary?.trend?.[0]?.student_tokens || 0) > 0 && (
                    <ReferenceLine
                      y={summary.trend[0].student_tokens}
                      stroke="#168cf5"
                      strokeDasharray="6 6"
                      strokeOpacity={0.55}
                      label={{
                        value: `${formatIndianNumber(summary.trend[0].student_tokens)} tokens`,
                        position: 'insideTopRight',
                        fill: '#168cf5',
                        fontSize: 11,
                        fontWeight: 700
                      }}
                    />
                  )}
                  <XAxis
                    dataKey="display_date"
                    stroke="#8fa0b5"
                    fontSize={10}
                    tickLine={false}
                    interval={4}
                  />
                  <YAxis
                    stroke="#8fa0b5"
                    fontSize={10}
                    tickLine={false}
                    tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                  />
                  <Tooltip
                    formatter={(val, name) => [
                      formatIndianNumber(val),
                      name === 'student_tokens' ? 'Student Tokens' : 'University Tokens'
                    ]}
                    labelFormatter={(lbl, payload) => payload?.[0]?.payload?.date || lbl}
                    contentStyle={{
                      background: '#ffffff',
                      border: '1px solid #dbe4ef',
                      borderRadius: '8px',
                      fontSize: '11px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="student_tokens"
                    stroke="#168cf5"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#studentGradient)"
                    dot={{ r: 4, strokeWidth: 2, fill: '#168cf5' }}
                    activeDot={{ r: 5 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="university_tokens"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#universityGradient)"
                    dot={{ r: 4, strokeWidth: 2, fill: '#10b981' }}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Chart 2: Cost Breakdown by Service Donut Chart */}
          <div className="analytics-card">
            <div className="analytics-header">
              <div>
                <h3 className="analytics-title">Cost Breakdown by Service</h3>
                <span className="analytics-subtitle">Actual USD cost</span>
              </div>
              <span className="cost-total-badge">{formatUSD(totalCostUsdValue)}</span>
            </div>
            {services.length === 1 && services[0]?.is_historical && (
              <div className="analytics-note">Historical usage pre-dates cost tracking — per-service cost appears once new AI calls are logged</div>
            )}
            <div className="donut-chart-layout">
              <div className="pie-wrap">
                {services.length === 1 && services[0]?.is_historical ? (
                  /* Full honest ring for the historical bucket (100% of pre-logging usage) */
                  <svg viewBox="0 0 150 150" className="donut-ring-svg">
                    <defs>
                      <linearGradient id="historicalRingGrad" x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0%" stopColor="#168cf5" />
                        <stop offset="100%" stopColor="#7557ee" />
                      </linearGradient>
                    </defs>
                    <circle cx="75" cy="75" r="52" fill="none" stroke="#eef2f7" strokeWidth="22" />
                    <circle
                      cx="75"
                      cy="75"
                      r="52"
                      fill="none"
                      stroke="url(#historicalRingGrad)"
                      strokeWidth="22"
                      strokeDasharray="325.6"
                      strokeDashoffset="0"
                      transform="rotate(-90 75 75)"
                    />
                  </svg>
                ) : (
                  <ResponsiveContainer width={150} height={150}>
                    <PieChart>
                      <Pie
                        data={services}
                        dataKey="cost_usd"
                        nameKey="service_name"
                        innerRadius={45}
                        outerRadius={68}
                        paddingAngle={3}
                      >
                        {services.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={SERVICE_COLORS[index % SERVICE_COLORS.length]}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(val, name) => [formatUSD(val), name]}
                        contentStyle={{
                          background: '#ffffff',
                          border: '1px solid #dbe4ef',
                          borderRadius: '8px',
                          fontSize: '11px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                )}
                <div className="donut-center-label">
                  <span className="center-amount">{formatUSD(totalCostUsdValue)}</span>
                  <span className="center-sub">Total</span>
                </div>
              </div>

              {/* Legend List */}
              <div className="service-legend-list">
                {services.map((s, idx) => (
                  <div className="service-legend-row" key={idx}>
                    <div className="service-name-wrap">
                      <span
                        className="service-dot"
                        style={{ background: SERVICE_COLORS[idx % SERVICE_COLORS.length] }}
                      ></span>
                      <span className="service-name">{s.service_name}</span>
                    </div>
                    <div className="service-amount-wrap">
                      <strong>{formatUSD(s.cost_usd)}</strong>
                      <span className="service-pct">({s.percentage}%)</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Chart 3: Tokens by Model Horizontal Bar Chart */}
          <div className="analytics-card">
            <div className="analytics-header">
              <div>
                <h3 className="analytics-title">Tokens by Model</h3>
                <span className="analytics-subtitle">Actual models deployed</span>
              </div>
              <span className="models-count-chip">{models.length} Models</span>
            </div>
            {models.length > 0 && models.every((m) => !m.total_tokens) && (
              <div className="analytics-note">No per-model usage logged yet — appears once new AI calls are made</div>
            )}
            <div className="model-bars-container">
              {models.slice(0, 5).map((m, idx) => {
                const maxTokens = Math.max(...models.map((x) => x.total_tokens || 1), 1);
                const pct = Math.min(100, Math.round((m.total_tokens / maxTokens) * 100));
                return (
                  <div className="model-bar-item" key={idx}>
                    <div className="model-bar-top">
                      <div className="model-title-group">
                        <span className="model-badge-provider">{m.provider}</span>
                        <span className="model-name-text">{m.name}</span>
                      </div>
                      <span className="model-tokens-count">{formatIndianNumber(m.total_tokens)}</span>
                    </div>
                    <div className={`model-progress-track ${pct > 0 ? '' : 'track-empty'}`}>
                      <div
                        className="model-progress-fill"
                        style={{
                          width: `${pct}%`,
                          background:
                            idx === 0
                              ? 'linear-gradient(90deg, #168cf5, #38bdf8)'
                              : idx === 1
                              ? 'linear-gradient(90deg, #7557ee, #a855f7)'
                              : idx === 2
                              ? 'linear-gradient(90deg, #10b981, #34d399)'
                              : idx === 3
                              ? 'linear-gradient(90deg, #f59e0b, #fbbf24)'
                              : 'linear-gradient(90deg, #64748b, #94a3b8)'
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Tabs Navigation Bar */}
        <div className="tabs-header-bar">
          <div className="tabs-buttons-list">
            <button
              className={`tab-item-btn ${activeTab === 'university' ? 'active' : ''}`}
              onClick={() => setActiveTab('university')}
            >
              <Building2 size={15} /> University Wise
            </button>
            <button
              className={`tab-item-btn ${activeTab === 'model' ? 'active' : ''}`}
              onClick={() => setActiveTab('model')}
            >
              <Cpu size={15} /> Model Wise
            </button>
            <button
              className={`tab-item-btn ${activeTab === 'service' ? 'active' : ''}`}
              onClick={() => setActiveTab('service')}
            >
              <Layers size={15} /> Service Wise
            </button>
            <button
              className={`tab-item-btn ${activeTab === 'student' ? 'active' : ''}`}
              onClick={() => setActiveTab('student')}
            >
              <Users size={15} /> Student Wise
            </button>
            <button
              className={`tab-item-btn ${activeTab === 'logs' ? 'active' : ''}`}
              onClick={() => setActiveTab('logs')}
            >
              Detailed Usage Logs
            </button>
          </div>

          <div className="tab-search-box">
            <Search size={14} className="tab-search-icon" />
            <input
              type="text"
              placeholder={`Search ${activeTab} data...`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="tab-search-input"
            />
            {search && (
              <button className="clear-search-btn" onClick={() => setSearch('')}>
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Primary Data Tables */}
        <div className="primary-table-card">
          {/* TAB 1: UNIVERSITY WISE */}
          {activeTab === 'university' && (
            <div className="table-responsive-wrapper">
              <table className="costing-data-table">
                <thead>
                  <tr>
                    <th style={{ width: '45px' }}>#</th>
                    <th>University Name</th>
                    <th>Students Count</th>
                    <th>University Tokens</th>
                    <th>Student Tokens</th>
                    <th>Total Tokens</th>
                    <th>Cost (USD)</th>
                    <th>Cost (INR)</th>
                    <th style={{ width: '90px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUniRows.length > 0 ? (
                    filteredUniRows.map((r, i) => (
                      <tr key={r.id || i}>
                        <td>{i + 1}</td>
                        <td>
                          <div className="uni-cell-name">
                            <Building2 size={14} className="cell-uni-icon" />
                            <strong>{r.university_name}</strong>
                          </div>
                        </td>
                        <td>
                          <span className="badge-count">{r.students_count} students</span>
                        </td>
                        <td>{formatIndianNumber(r.university_tokens)}</td>
                        <td>{formatIndianNumber(r.student_tokens)}</td>
                        <td>
                          <strong>{formatIndianNumber(r.total_tokens)}</strong>
                        </td>
                        <td>
                          <span className="cost-usd-cell">{formatUSD(r.cost_usd)}</span>
                        </td>
                        <td>
                          <span className="cost-inr-cell">{formatINR(r.cost_inr)}</span>
                        </td>
                        <td>
                          <button
                            className="table-action-view-btn"
                            onClick={() => openUniversityModal(r)}
                            title="View University Breakdown"
                          >
                            <Eye size={13} /> View
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="no-data-cell">
                        No universities found matching your filter criteria.
                      </td>
                    </tr>
                  )}
                </tbody>

                {/* PINNED TOTAL SUMMARY ROW */}
                {uniCostingData.total_summary && (
                  <tfoot>
                    <tr className="pinned-total-row">
                      <td>∑</td>
                      <td>
                        <strong>{uniCostingData.total_summary.university_name}</strong>
                      </td>
                      <td>
                        <strong className="badge-count total-badge">
                          {uniCostingData.total_summary.students_count} students
                        </strong>
                      </td>
                      <td>
                        <strong>{formatIndianNumber(uniCostingData.total_summary.university_tokens)}</strong>
                      </td>
                      <td>
                        <strong>{formatIndianNumber(uniCostingData.total_summary.student_tokens)}</strong>
                      </td>
                      <td>
                        <strong className="total-highlight">
                          {formatIndianNumber(uniCostingData.total_summary.total_tokens)}
                        </strong>
                      </td>
                      <td>
                        <strong className="cost-usd-highlight">
                          {formatUSD(uniCostingData.total_summary.cost_usd)}
                        </strong>
                      </td>
                      <td>
                        <strong className="cost-inr-highlight">
                          {formatINR(uniCostingData.total_summary.cost_inr)}
                        </strong>
                      </td>
                      <td>—</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          )}

          {/* TAB 2: MODEL WISE */}
          {activeTab === 'model' && (
            <div className="table-responsive-wrapper">
              {filteredModelRows.some((m) => m.is_historical) && (
                <div className="analytics-note">Historical usage attributed to the primary model — per-model detail appears once new AI calls are logged</div>
              )}
              <table className="costing-data-table">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th>Model Name</th>
                    <th>Actual Model ID</th>
                    <th>Input Tokens</th>
                    <th>Cached Tokens</th>
                    <th>Output Tokens</th>
                    <th>Total Tokens</th>
                    <th>Cost USD</th>
                    <th>Cost INR</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredModelRows.map((m, i) => (
                    <tr key={i}>
                      <td>
                        <span className="provider-pill">{m.provider}</span>
                      </td>
                      <td>
                        <strong>{m.name}</strong>
                      </td>
                      <td>
                        <code className="code-id">{m.id}</code>
                      </td>
                      <td>{formatIndianNumber(m.input_tokens)}</td>
                      <td>{formatIndianNumber(m.cached_input_tokens)}</td>
                      <td>{formatIndianNumber(m.output_tokens)}</td>
                      <td>
                        <strong>{formatIndianNumber(m.total_tokens)}</strong>
                      </td>
                      <td>
                        <span className="cost-usd-cell">{formatUSD(m.cost_usd)}</span>
                      </td>
                      <td>
                        <span className="cost-inr-cell">{formatINR(m.cost_inr)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 3: SERVICE WISE */}
          {activeTab === 'service' && (
            <div className="table-responsive-wrapper">
              <table className="costing-data-table">
                <thead>
                  <tr>
                    <th>AI Service Name</th>
                    <th>Total Tokens</th>
                    <th>Cost USD</th>
                    <th>Cost INR</th>
                    <th>% of AI Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredServiceRows.map((s, i) => (
                    <tr key={i}>
                      <td>
                        <div className="uni-cell-name">
                          <Layers size={14} className="cell-uni-icon" />
                          <strong>{s.service_name}</strong>
                        </div>
                      </td>
                      <td>{formatIndianNumber(s.total_tokens)}</td>
                      <td>
                        <span className="cost-usd-cell">{formatUSD(s.cost_usd)}</span>
                      </td>
                      <td>
                        <span className="cost-inr-cell">{formatINR(s.cost_inr)}</span>
                      </td>
                      <td>
                        <div className="pct-progress-cell">
                          <div className="pct-bar">
                            <span style={{ width: `${s.percentage}%` }}></span>
                          </div>
                          <span>{s.percentage}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 4: STUDENT WISE */}
          {activeTab === 'student' && (
            <div className="table-responsive-wrapper">
              <table className="costing-data-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Student Name</th>
                    <th>University</th>
                    <th>Total Tokens</th>
                    <th>Cost USD</th>
                    <th>Cost INR</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStudentRows.map((st, i) => (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>
                        <strong>{st.student_name}</strong>
                      </td>
                      <td>{st.university_name}</td>
                      <td>
                        <strong>{formatIndianNumber(st.total_tokens)}</strong>
                      </td>
                      <td>{formatUSD(st.cost_usd)}</td>
                      <td>{formatINR(st.cost_inr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* TAB 5: DETAILED USAGE LOGS */}
          {activeTab === 'logs' && (
            <div className="table-responsive-wrapper">
              <table className="costing-data-table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>University</th>
                    <th>User</th>
                    <th>Service</th>
                    <th>Model</th>
                    <th>Input</th>
                    <th>Cached</th>
                    <th>Output</th>
                    <th>Total</th>
                    <th>USD</th>
                    <th>INR</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogRows.slice(0, 100).map((l, i) => (
                    <tr key={l.id || i}>
                      <td>{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</td>
                      <td>{l.university_name}</td>
                      <td>
                        <span
                          className={`user-type-tag ${l.user_type === 'STUDENT' ? 'tag-student' : 'tag-uni'}`}
                        >
                          {l.user_type === 'STUDENT' ? l.student_name || 'Student' : 'University'}
                        </span>
                      </td>
                      <td>{l.service_name}</td>
                      <td>{l.model_name || l.actual_model_id}</td>
                      <td>{formatIndianNumber(l.input_tokens)}</td>
                      <td>{formatIndianNumber(l.cached_input_tokens)}</td>
                      <td>{formatIndianNumber(l.output_tokens)}</td>
                      <td>
                        <strong>{formatIndianNumber(l.total_tokens)}</strong>
                      </td>
                      <td>{formatUSD(l.cost_usd)}</td>
                      <td>{formatINR(l.cost_inr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 3 Bottom Granular Cards */}
        <div className="bottom-cards-grid">
          {/* Card 1: University AI Services Usage */}
          <div className="bottom-card">
            <div className="bottom-card-header">
              <h4 className="bottom-card-title">University AI Services Usage</h4>
              <select
                value={selectedBottomUni}
                onChange={(e) => setSelectedBottomUni(e.target.value)}
                className="bottom-uni-picker"
              >
                {universities.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="bottom-table-wrap">
              <table className="bottom-compact-table">
                <thead>
                  <tr>
                    <th>Service</th>
                    <th>Model Used</th>
                    <th>Tokens</th>
                    <th>USD</th>
                    <th>INR</th>
                  </tr>
                </thead>
                <tbody>
                  {bottomUniServices.length > 0 ? (
                    bottomUniServices.map((x, i) => (
                      <tr key={i}>
                        <td>
                          <strong>{x.service_name}</strong>
                        </td>
                        <td>
                          <span className="compact-model-tag">
                            {[...x.models].join(', ') || 'Various'}
                          </span>
                        </td>
                        <td>{formatIndianNumber(x.tokens)}</td>
                        <td>{formatUSD(x.usd)}</td>
                        <td>{formatINR(x.inr)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} className="no-data-cell">
                        No usage recorded for this university.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Card 2: Student Usage */}
          <div className="bottom-card">
            <div className="bottom-card-header">
              <h4 className="bottom-card-title">Top Student Usage</h4>
              <span className="bottom-card-subtitle">By token volume</span>
            </div>
            <div className="bottom-table-wrap">
              <table className="bottom-compact-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>University</th>
                    <th>Tokens</th>
                    <th>USD</th>
                    <th>INR</th>
                  </tr>
                </thead>
                <tbody>
                  {bottomStudents.map((st, i) => (
                    <tr key={i}>
                      <td>
                        <strong>{st.student_name}</strong>
                      </td>
                      <td>
                        <span className="uni-subname">{st.university_name}</span>
                      </td>
                      <td>{formatIndianNumber(st.total_tokens)}</td>
                      <td>{formatUSD(st.cost_usd)}</td>
                      <td>{formatINR(st.cost_inr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Card 3: Model Usage */}
          <div className="bottom-card">
            <div className="bottom-card-header">
              <h4 className="bottom-card-title">Model Usage</h4>
              <span className="bottom-card-subtitle">Active LLM engines</span>
            </div>
            {bottomModels.some((m) => m.is_historical) && (
              <div className="analytics-note">Historical usage attributed to the primary model — per-model detail appears once new AI calls are logged</div>
            )}
            <div className="bottom-table-wrap">
              <table className="bottom-compact-table">
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Provider</th>
                    <th>Tokens</th>
                    <th>USD</th>
                    <th>INR</th>
                  </tr>
                </thead>
                <tbody>
                  {bottomModels.map((m, i) => (
                    <tr key={i}>
                      <td>
                        <strong>{m.name}</strong>
                      </td>
                      <td>
                        <span className="provider-subpill">{m.provider}</span>
                      </td>
                      <td>{formatIndianNumber(m.total_tokens)}</td>
                      <td>{formatUSD(m.cost_usd)}</td>
                      <td>{formatINR(m.cost_inr)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Footer Bar */}
        <div className="costing-footer-strip">
          <div className="footer-left-note">
            <Info size={14} className="footer-info-icon" />
            <span>
              Model prices are synced directly from{' '}
              <a
                href="https://openrouter.ai/workspaces/default"
                target="_blank"
                rel="noopener noreferrer"
              >
                OpenRouter Workspace API
              </a>
              . Calculation incorporates OpenRouter {summary?.fee_percentage != null ? `${summary.fee_percentage}%` : ''} platform fee.
            </span>
          </div>
          <div className="footer-right-rates">
            <span className="rate-item">
              Exchange Rate: <strong>{summary?.usd_inr_rate ? `1 USD = ₹ ${summary.usd_inr_rate.toFixed(2)}` : '—'}</strong>
            </span>
            <span className="sep-divider">|</span>
            <span className="rate-item">
              Last Updated:{' '}
              <strong>
                {summary?.last_updated
                  ? new Date(summary.last_updated).toLocaleDateString()
                  : '—'}
              </strong>
            </span>
          </div>
        </div>
      </div>

      {/* University Modal */}
      {modalOpen && modalUniversity && (
        <div
          className="modal-overlay-bg"
          onClick={(e) => {
            if (e.target === e.currentTarget) setModalOpen(false);
          }}
        >
          <div className="uni-details-modal">
            <div className="modal-top-bar">
              <div className="modal-title-wrap">
                <Building2 size={22} className="modal-building-icon" />
                <div>
                  <h3 className="modal-heading">{modalUniversity.university_name}</h3>
                  <p className="modal-subheading">
                    Detailed AI Consumption, Token Distribution &amp; Cost Breakdown
                  </p>
                </div>
              </div>
              <button className="modal-close-icon-btn" onClick={() => setModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body-content">
              {/* Modal Mini KPIs */}
              <div className="modal-kpi-row">
                <div className="modal-mini-kpi">
                  <span className="mini-label">Total Tokens</span>
                  <strong className="mini-val">{formatIndianNumber(modalStats.tokens)}</strong>
                </div>
                <div className="modal-mini-kpi">
                  <span className="mini-label">University Tokens</span>
                  <strong className="mini-val">{formatIndianNumber(modalStats.ut)}</strong>
                </div>
                <div className="modal-mini-kpi">
                  <span className="mini-label">Student Tokens</span>
                  <strong className="mini-val">{formatIndianNumber(modalStats.st)}</strong>
                </div>
                <div className="modal-mini-kpi">
                  <span className="mini-label">Cost (USD)</span>
                  <strong className="mini-val cost-usd-cell">{formatUSD(modalStats.usd)}</strong>
                </div>
                <div className="modal-mini-kpi">
                  <span className="mini-label">Cost (INR)</span>
                  <strong className="mini-val cost-inr-cell">{formatINR(modalStats.inr)}</strong>
                </div>
              </div>

              <div className="modal-table-section">
                <h4 className="modal-section-title">Recent Usage Logs for this University</h4>
                <div className="modal-table-scroll">
                  <table className="costing-data-table modal-inner-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>User Type</th>
                        <th>Service</th>
                        <th>Actual Model</th>
                        <th>Input Tokens</th>
                        <th>Cached</th>
                        <th>Output Tokens</th>
                        <th>Total Tokens</th>
                        <th>Cost (USD)</th>
                        <th>Cost (INR)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modalStats.logs.slice(0, 40).map((x, i) => (
                        <tr key={i}>
                          <td>{x.created_at ? new Date(x.created_at).toLocaleDateString() : '—'}</td>
                          <td>
                            <span
                              className={`user-type-tag ${
                                x.user_type === 'STUDENT' ? 'tag-student' : 'tag-uni'
                              }`}
                            >
                              {x.user_type === 'STUDENT' ? x.student_name || 'Student' : 'University'}
                            </span>
                          </td>
                          <td>{x.service_name}</td>
                          <td>{x.model_name || x.actual_model_id}</td>
                          <td>{formatIndianNumber(x.input_tokens)}</td>
                          <td>{formatIndianNumber(x.cached_input_tokens)}</td>
                          <td>{formatIndianNumber(x.output_tokens)}</td>
                          <td>
                            <strong>{formatIndianNumber(x.total_tokens)}</strong>
                          </td>
                          <td>{formatUSD(x.cost_usd)}</td>
                          <td>{formatINR(x.cost_inr)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { OpenRouterCosting };

const AiCosting = () => {
  const [vendorTab, setVendorTab] = useState('openrouter'); // 'openrouter' | 'beyondpresence'

  return (
    <div className="ai-costing-vendor-tabs-wrapper" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' }}>
        <button 
          onClick={() => setVendorTab('openrouter')}
          style={{
            padding: '10px 20px',
            background: vendorTab === 'openrouter' ? '#f8fafc' : 'transparent',
            border: vendorTab === 'openrouter' ? '1px solid #cbd5e1' : '1px solid transparent',
            borderBottom: vendorTab === 'openrouter' ? '2px solid #168cf5' : 'none',
            borderRadius: '6px 6px 0 0',
            fontWeight: vendorTab === 'openrouter' ? '600' : '400',
            color: vendorTab === 'openrouter' ? '#0f172a' : '#64748b',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          OpenRouter Costing
        </button>
        <button 
          onClick={() => setVendorTab('beyondpresence')}
          style={{
            padding: '10px 20px',
            background: vendorTab === 'beyondpresence' ? '#f8fafc' : 'transparent',
            border: vendorTab === 'beyondpresence' ? '1px solid #cbd5e1' : '1px solid transparent',
            borderBottom: vendorTab === 'beyondpresence' ? '2px solid #7c3aed' : 'none',
            borderRadius: '6px 6px 0 0',
            fontWeight: vendorTab === 'beyondpresence' ? '600' : '400',
            color: vendorTab === 'beyondpresence' ? '#0f172a' : '#64748b',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
        >
          Beyond Presence Costing
        </button>
      </div>

      <div className="ai-costing-vendor-content" style={{ marginTop: '-21px' }}>
        {vendorTab === 'openrouter' && <OpenRouterCosting />}
        {vendorTab === 'beyondpresence' && <BeyondPresenceCosting />}
      </div>
    </div>
  );
};

export default AiCosting;
